import React, { useState, useRef, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import axios from 'axios';
import { url, api } from '../../../../../env';
import { AppTextField, AppNumberField, AppTextareaField } from '../../../../../Components/FormKit';

const cardHeader = (title) => (
    <div style={{
        background: '#f8fafc',
        padding: '0.65rem 1rem',
        borderBottom: '1px solid #e2e8f0',
        borderRadius: '14px 14px 0 0',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
    }}>
        <span style={{ fontWeight: 700, fontSize: '0.72rem', color: '#1565C0', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
            {title}
        </span>
        <div style={{ flex: 1, height: '1px', background: '#bfdbfe' }} />
    </div>
);

export const InitialPaymentModal = ({ visible, onHide, persona, toast, stepStatus, stepData, onSuccess }) => {
    const isCompleted = stepStatus === 'completed';
    const [email, setEmail] = useState('');
    // Generar asunto dinámico
    const vehicleModel = persona?.infoBudget?.vehicle?.model || 'vehículo';
    const vehicleBrand = persona?.infoBudget?.vehicle?.brand || '';
    const vehicleFullName = vehicleBrand ? `${vehicleBrand} ${vehicleModel}` : vehicleModel;

    const [subject, setSubject] = useState(`Confirmación de registro pago inicial - ${vehicleFullName}`);
    const [emailContent, setEmailContent] = useState('');
    const [verifiedAmount, setVerifiedAmount] = useState('');
    const [expectedAmount, setExpectedAmount] = useState(0);

    // Default content generator
    useEffect(() => {
        if (persona) {
            const rawDownPayment = persona?.infoBudget?.financial_details?.groupConceptsEnganche.totalWitIva || 0;
            const downPayment = Math.round(rawDownPayment * 100) / 100;
            // Calcular restante si existen pagos previos
            let currentExpected = downPayment;
            if (stepData && stepData.total_paid) {
                currentExpected = downPayment - parseFloat(stepData.total_paid);
                // Si el restante es negativo o cero, ponerlo a 0 (aunque el estado debería ser completed)
                if (currentExpected < 0) currentExpected = 0;
            }
            setExpectedAmount(currentExpected);
            // Intentar prellenar con 0 por defecto
            //si el pago esta en pending y existe entonces agarrar ese pago como el verifiedAmount
            const pendingPayment = stepData?.payments?.find(p => p.status === 'pending');
            if (pendingPayment) {
                setVerifiedAmount(pendingPayment.amount);
            } else {
                setVerifiedAmount(currentExpected);
            }
            generateEmailTemplate(currentExpected, currentExpected);
            setEmail(persona.email || '');
        }
    }, [persona, vehicleFullName, stepData]); // Added stepData dependency


    const generateEmailTemplate = (verified, expected) => {
        const clientName = persona?.name || 'Cliente';
        const formattedExpected = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(expected);
        const formattedVerified = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(verified);
        const diff = expected - verified;
        const formattedDiff = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(diff);

        let newSubject = `✅ Confirmación de registro pago inicial - ${vehicleFullName}`;
        let newContent = '';

        if (verified < expected - 1) { // -1 para margen de error
            newSubject = `⚠️ Aviso de pago incompleto - ${vehicleFullName}`;
            newContent = `Hola ${clientName} 👋,

Hemos recibido tu comprobante de pago por la cantidad de ${formattedVerified}.

Sin embargo, el pago total era de ${formattedExpected}
existe un saldo pendiente de ${formattedDiff} 📉.

Por favor, realiza el pago del monto restante y sube el nuevo comprobante para que podamos continuar con el proceso de facturación 🧾.

Saludos,
Equipo Peicar 🚗`;
        } else if (verified > expected) { // Caso de sobrepago
            const excess = verified - expected;
            const formattedExcess = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(excess);

            newContent = `Hola ${clientName} 👋,

Hemos recibido tu comprobante de pago por la cantidad de ${formattedVerified} para tu ${vehicleFullName} 🚗.

Dado que el monto requerido era de ${formattedExpected}, hemos registrado un excedente de ${formattedExcess} 🎉.
Este excedente se verá reflejado como un ajuste en tu próxima mensualidad.

Tu comprobante ha sido cargado exitosamente y procederemos a generar tu factura a la brevedad 🧾.

Saludos,
Equipo Peicar`;

        } else {
            newContent = `Hola ${clientName} 👋,

Hemos recibido tu comprobante de pago por la cantidad de ${formattedVerified} para tu ${vehicleFullName} 🚗.

Tu comprobante ha sido cargado exitosamente en nuestro sistema y procederemos a generar tu factura a la brevedad 🧾.

Saludos,
Equipo Peicar`;
        }

        setSubject(newSubject);
        setEmailContent(newContent);
    };

    const handleAmountChange = (value) => {
        setVerifiedAmount(value);
        const numValue = parseFloat(value) || 0;

        // Si hay un pago pendiente y el monto verificado es diferente al reclamado, es una corrección
        const pendingPayment = stepData?.payments?.find(p => p.status === 'pending');
        if (pendingPayment && numValue !== parseFloat(pendingPayment.amount)) {
            generateCorrectionTemplate(numValue, pendingPayment.amount, expectedAmount);
        } else {
            generateEmailTemplate(numValue, expectedAmount);
        }
    };

    const generateCorrectionTemplate = (verified, claimed, expected) => {
        const clientName = persona?.name || 'Cliente';
        const formattedClaimed = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(claimed);
        const formattedVerified = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(verified);
        const formattedExpected = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(expected);

        setSubject(`⚠️ Notificación de corrección en pago inicial - ${vehicleFullName}`);
        setEmailContent(`Hola ${clientName} 👋,

Hemos revisado el comprobante de pago que cargaste por ${formattedClaimed}.

Tras la verificación, el monto registrado ha sido corregido a ${formattedVerified}.
El monto total requerido para este paso es de ${formattedExpected}.

Tu proceso continuará basándose en el monto verificado. Si tienes alguna duda, por favor contacta a tu asesor.

Saludos,
Equipo Peicar`);
    };

    const generateRejectionTemplate = () => {
        const clientName = persona?.name || 'Cliente';
        const pendingPayment = stepData?.payments?.find(p => p.status === 'pending');
        const formattedClaimed = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(pendingPayment?.amount || 0);

        setSubject(`❌ Pago inicial rechazado - ${vehicleFullName}`);
        setEmailContent(`Hola ${clientName} 👋,

Lamentamos informarte que el comprobante de pago cargado recientemente por ${formattedClaimed} ha sido rechazado.

Motivo: [Ingresa aquí la razón del rechazo]

Por favor, revisa tu comprobante y vuelve a subirlo desde tu dashboard para continuar con el proceso.

Saludos,
Equipo Peicar`);
    };

    const [ccEmails, setCcEmails] = useState([]);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [sending, setSending] = useState(false);
    const fileInputRef = useRef(null);

    // Cargar datos si ya está completado
    useEffect(() => {
        if (visible && isCompleted && stepData?.metadata) {
            setEmail(stepData.metadata.email_sent_to || '');
            setSubject(stepData.metadata.subject || '');
            setEmailContent(stepData.metadata.content || '');
        }
    }, [visible, isCompleted, stepData]);

    // Validación de campos
    const validateFields = () => {
        const errors = [];

        if (!verifiedAmount || parseFloat(verifiedAmount) <= 0) {
            errors.push('El monto verificado debe ser mayor a 0');
        }

        if (!email || email.trim() === '') {
            errors.push('El correo del destinatario es requerido');
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
                errors.push('El correo del destinatario no es válido');
            }
        }

        if (!subject || subject.trim() === '') {
            errors.push('El asunto es requerido');
        }

        if (!emailContent || emailContent.trim() === '') {
            errors.push('El contenido del correo es requerido');
        }
        // Validar si es necesario subir archivo
        // No es necesario si:
        // 1. Ya está completado (isCompleted is true)
        // 2. Hay un pago pendiente que se está verificando (stepData?.payments?.some(p => p.status === 'pending'))
        const hasPendingPayment = stepData?.payments?.some(p => p.status === 'pending');

        if (!isCompleted && uploadedFiles.length === 0 && !hasPendingPayment) {
            errors.push('Debes subir al menos un comprobante de pago');
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        ccEmails.forEach((cc, index) => {
            if (cc && cc.trim() !== '' && !emailRegex.test(cc)) {
                errors.push(`El correo CC #${index + 1} no es válido`);
            }
        });

        if (!persona?.id) {
            errors.push('No se pudo identificar el cliente');
        }

        return errors;
    };

    const handleSendEmail = async () => {
        const errors = validateFields();
        if (errors.length > 0) {
            errors.forEach(error => {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error de validación',
                    detail: error,
                    life: 4000
                });
            });
            return;
        }

        setSending(true);

        try {
            const formData = new FormData();
            formData.append('email', email);
            formData.append('subject', subject);
            formData.append('content', emailContent);
            formData.append('persona_id', persona.id);
            formData.append('budget_id', persona.infoBudget.budget_id);
            formData.append('typePersona', persona.tipo_persona);
            formData.append('verified_amount', verifiedAmount || 0);
            formData.append('expected_amount', expectedAmount || 0);

            const pendingPayment = stepData?.payments?.find(p => p.status === 'pending');
            if (pendingPayment) {
                formData.append('pending_payment_id', pendingPayment.id);
            }

            const validCc = ccEmails.filter(cc => cc && cc.trim() !== '');
            validCc.forEach((cc, index) => {
                formData.append(`cc[${index}]`, cc);
            });

            // Agregar archivos adjuntos
            uploadedFiles.forEach((file, index) => {
                formData.append(`attachments[${index}]`, file);
            });

            const token = localStorage.getItem('token');
            const response = await axios.post(`${url}/send-initial-payment-email`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json'
                }
            });

            if (response.data.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Comprobante subido',
                    detail: 'El comprobante se ha subido y enviado exitosamente',
                    life: 3000
                });

                if (onSuccess && response.data.data) {
                    onSuccess(response.data.data);
                }

                onHide();
            }

        } catch (error) {
            console.error('Error al subir comprobante:', error);
            const errorMessage = error.response?.data?.message || 'Error al subir el comprobante';
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: errorMessage,
                life: 4000
            });
        } finally {
            setSending(false);
        }
    };

    const handleRejectPayment = async () => {
        const pendingPayment = stepData?.payments?.find(p => p.status === 'pending');
        if (!pendingPayment) return;

        const errors = [];
        // Check explicitly if the default placeholder is still there
        if (emailContent.includes('[Ingresa aquí la razón del rechazo]')) {
            errors.push('Te falta ingresar una razón en el correo. Texto encontrado: "[Ingresa aquí la razón del rechazo]"');
        } else if (!emailContent.includes('Motivo:')) {
            errors.push('El correo debe contener "Motivo:" seguido de la explicación.');
        }

        if (errors.length > 0) {
            errors.forEach(err => toast.current?.show({ severity: 'error', summary: 'Error', detail: err }));
            return;
        }

        setSending(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${url}/reject-initial-payment`, {
                payment_id: pendingPayment.id,
                comment: emailContent,
                persona_id: persona.id,
                email: email,
                subject: subject,
                content: emailContent,
                cc: ccEmails.filter(cc => cc && cc.trim() !== '')
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json'
                }
            });

            if (response.data.success) {
                toast.current?.show({ severity: 'success', summary: 'Pago Rechazado', detail: 'Se ha notificado al usuario' });
                if (onSuccess) onSuccess(response.data.data);
                onHide();
            }
        } catch (error) {
            console.error('Error rejecting payment:', error);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Error al rechazar el pago' });
        } finally {
            setSending(false);
        }
    };

    const handleFileSelect = (e) => {
        const added = Array.from(e.target.files || []);
        const updated = [...uploadedFiles, ...added];
        setUploadedFiles(updated);
        e.target.value = '';
    };

    const removeFile = (index) => {
        setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
    };

    const addCcEmail = () => {
        setCcEmails([...ccEmails, '']);
    };

    const updateCcEmail = (index, value) => {
        const newCcEmails = [...ccEmails];
        newCcEmails[index] = value;
        setCcEmails(newCcEmails);
    };

    const removeCcEmail = (index) => {
        setCcEmails(ccEmails.filter((_, i) => i !== index));
    };

    const totalRemaining = () => {
        // Exclude rejected and pending payments from total paid
        const totalPaid = Number(stepData?.payments?.filter(p => p.status === 'complete' || p.status === 'incomplete')?.reduce((total, payment) => total + Number(payment.amount), 0) || 0);
        const totalWitIva = Number(persona?.infoBudget?.financial_details?.groupConceptsEnganche.totalWitIva || 0);
        const rem = totalWitIva - totalPaid;
        return rem < 0 ? 0 : rem; // Ensure not negative
    };
    const totalPaid = () => {
        // Exclude rejected and pending payments from total paid
        const totalPaid = Number(stepData?.payments?.filter(p => p.status === 'complete' || p.status === 'incomplete')?.reduce((total, payment) => total + Number(payment.amount), 0) || 0);
        return totalPaid;
    };

    const clientFiles = stepData?.files?.filter(f => f.uploaded_by_type === 'user') || [];
    const adminFiles = stepData?.files?.filter(f => f.uploaded_by_type === 'admin') || [];

    // Filter files specifically for the pending evaluation block
    const pendingPaymentElement = stepData?.payments?.find(p => p.status === 'pending');
    const currentClientFiles = pendingPaymentElement ? clientFiles.filter(f => {
        const fileDate = new Date(f.created_at);
        const paymentDate = new Date(pendingPaymentElement.created_at);
        // Allow a generous window (e.g. 5 minutes) in case of slight server delays, or if uploaded shortly before
        const diff = Math.abs(fileDate - paymentDate);
        return diff < 5 * 60 * 1000;
    }) : [];

    const hasPaymentsHistory = stepData?.payments && stepData.payments.length > 0;

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            style={{ width: '50vw', maxWidth: '900px', minWidth: '600px', opacity: "inherit", boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}
            breakpoints={{ '960px': '90vw', '641px': '95vw' }}
            contentStyle={{ maxHeight: '75vh', overflowY: 'auto', overscrollBehavior: 'contain' }}
            header={
                <div className="flex align-items-center gap-2">
                    <h2
                        style={{
                            background: "linear-gradient(90deg, #21588C 6.22%, #21588C 77.34%)",
                            WebkitBackgroundClip: "text",
                            backgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            margin: 0
                        }}
                    >
                        {isCompleted ? 'Detalle de Pago Inicial' : 'Subir Pago Inicial'}
                    </h2>
                </div>
            }
            modal
            draggable={false}
        >
            <style>
                {`
                    /* Modal redondeado para coincidir con las tk-table-card */
                    .p-dialog {
                        background: #ffffff !important;
                        overflow: hidden !important;
                        border-radius: 16px !important;
                    }
                    .p-dialog-header {
                        background: #ffffff !important;
                        margin-bottom: 0 !important;
                        border-top-left-radius: 16px !important;
                        border-top-right-radius: 16px !important;
                    }
                    .p-dialog-content {
                        background: #ffffff !important;
                        margin-top: 0 !important;
                        border-bottom-left-radius: 16px !important;
                        border-bottom-right-radius: 16px !important;
                    }
                `}
            </style>

            <div className="email-modal-content fk-root flex flex-column gap-3">
                {/* Banners de Alerta */}
                {stepData?.metadata?.shortfall_amount > 0 && (
                    <div className="p-3 border-round" style={{
                        background: 'rgba(239,68,68,0.1)',
                        border: '1px solid #fecaca'
                    }}>
                        <div className="flex align-items-center gap-2">
                            <i className="pi pi-exclamation-circle text-red-500"></i>
                            <span className="font-medium text-red-700">
                                Se requiere pago adicional de {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(stepData.metadata.shortfall_amount)}
                            </span>
                        </div>
                        <p className="text-sm text-red-600 m-0 mt-1">
                            La cotización fue ajustada y el enganche aumentó. Se requiere cubrir la diferencia.
                        </p>
                    </div>
                )}

                {stepData?.metadata?.surplus_amount > 0 && (
                    <div className="p-3 border-round" style={{
                        background: 'rgba(16,185,129,0.1)',
                        border: '1px solid #a7f3d0'
                    }}>
                        <div className="flex align-items-center gap-2">
                            <i className="pi pi-info-circle text-green-500"></i>
                            <span className="font-medium text-green-700">
                                Saldo a favor de {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(stepData.metadata.surplus_amount)}
                            </span>
                        </div>
                        <p className="text-sm text-green-600 m-0 mt-1">
                            La cotización fue ajustada y el enganche disminuyó. El excedente se aplicará como ajuste.
                        </p>
                    </div>
                )}

                {stepData?.payments?.some(p => p.status === 'pending') && (
                    <div className="bg-yellow-50 border-left-3 border-yellow-500 p-3 flex flex-column md:flex-row justify-content-between align-items-start md:align-items-center gap-3 border-round">
                        <div>
                            <span className="font-bold block text-yellow-800">Evaluación Pendiente</span>
                            <span className="text-sm text-yellow-700">
                                El usuario ha cargado un comprobante reclamando un pago de:
                                <strong className="ml-1">
                                    {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(stepData.payments.find(p => p.status === 'pending').amount)}
                                </strong>
                            </span>
                            {currentClientFiles.length > 0 && (
                                <div className="mt-2">
                                    <span className="text-xs font-bold text-yellow-800 block mb-1">Comprobante(s) del Cliente:</span>
                                    <div className="flex flex-wrap gap-2">
                                        {currentClientFiles.map((file, index) => (
                                            <div key={index} className="flex align-items-center gap-2 p-2 border-1 border-yellow-300 border-round bg-white shadow-1" style={{ minWidth: '200px' }}>
                                                <i className="pi pi-file-pdf text-red-500 text-xl"></i>
                                                <span className="text-sm text-gray-800 cursor-pointer" title={file.file_name}>{file.file_name.substring(0, 30)}...</span>
                                                {file.s3_url && (
                                                    <a href={file.s3_url} target="_blank" rel="noopener noreferrer" className="ml-auto">
                                                        <i className="pi pi-eye text-blue-600 cursor-pointer"></i>
                                                    </a>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Button
                                label="Aceptar"
                                icon="pi pi-check"
                                className="p-button-success p-button-sm"
                                onClick={() => {
                                    const pending = stepData.payments.find(p => p.status === 'pending');
                                    handleAmountChange(pending.amount);
                                    toast.current?.show({ severity: 'info', summary: 'Monto actualizado', detail: 'Confirma el correo abajo y dale click a Confirmar y Enviar', life: 3000 });
                                }}
                                disabled={isCompleted}
                            />
                            <Button
                                label="Rechazar"
                                icon="pi pi-times"
                                className="p-button-danger p-button-sm"
                                onClick={generateRejectionTemplate}
                                disabled={isCompleted}
                            />
                        </div>
                    </div>
                )}

                {/* Card 1: Montos del Pago */}
                <div className="tk-table-card">
                    {cardHeader('Montos del Pago')}
                    <div style={{ padding: '1rem' }} className="flex flex-column gap-3">
                        <div className="grid">
                            <div className="col-12 md:col-6">
                                <AppNumberField
                                    label="Monto a pagar"
                                    value={expectedAmount}
                                    mode="currency"
                                    currency="MXN"
                                    locale="es-MX"
                                    minFractionDigits={2}
                                    maxFractionDigits={2}
                                    disabled
                                    readOnly
                                />
                            </div>
                            <div className="col-12 md:col-6">
                                <AppNumberField
                                    label="Monto verificado"
                                    value={verifiedAmount}
                                    onValueChange={(e) => handleAmountChange(e.value)}
                                    mode="currency"
                                    currency="MXN"
                                    locale="es-MX"
                                    placeholder="$ 0.00"
                                    disabled={isCompleted}
                                    invalid={verifiedAmount > expectedAmount + 1}
                                    errorMessage={verifiedAmount > expectedAmount + 1 ? '⚠️ El monto ingresado excede el saldo pendiente.' : undefined}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Card 2: Destinatario y Copias */}
                <div className="tk-table-card">
                    {cardHeader('Destinatario y Copias')}
                    <div style={{ padding: '1rem' }} className="flex flex-column gap-3">
                        <AppTextField
                            label="Correo del destinatario"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="correo@ejemplo.com"
                            disabled={isCompleted}
                        />

                        {/* CC */}
                        <div className="flex flex-column gap-2">
                            <div className="flex align-items-center justify-content-between">
                                <span className="fk-label" style={{ margin: 0 }}>CC — Con Copia</span>
                                {!isCompleted && (
                                    <button
                                        type="button"
                                        onClick={addCcEmail}
                                        className="fk-btn fk-btn--secondary fk-btn--sm"
                                        style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                                    >
                                        <i className="pi pi-plus" style={{ fontSize: '0.7rem' }} />
                                        Agregar CC
                                    </button>
                                )}
                            </div>
                            {ccEmails.map((ccEmail, index) => (
                                <div key={index} className="flex gap-2 align-items-center">
                                    <div className="fk-input flex-1">
                                        <input
                                            className="p-inputtext"
                                            value={ccEmail}
                                            onChange={(e) => updateCcEmail(index, e.target.value)}
                                            placeholder="destinatario@ejemplo.com"
                                            disabled={isCompleted}
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                    {!isCompleted && (
                                        <button
                                            type="button"
                                            className="fk-icon-btn fk-icon-btn--danger"
                                            onClick={() => removeCcEmail(index)}
                                            title="Eliminar"
                                        >
                                            <i className="pi pi-times" style={{ fontSize: '0.8rem' }} />
                                        </button>
                                    )}
                                </div>
                            ))}
                            {ccEmails.length === 0 && (
                                <span style={{ fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic' }}>
                                    Sin destinatarios en copia
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Card 3: Configuración del Correo */}
                <div className="tk-table-card">
                    {cardHeader('Configuración del Correo')}
                    <div style={{ padding: '1rem' }} className="flex flex-column gap-3">
                        <AppTextField
                            label="Asunto"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="Asunto del correo"
                            disabled={isCompleted}
                        />
                        <AppTextareaField
                            label="Contenido del correo"
                            value={emailContent}
                            onChange={(e) => setEmailContent(e.target.value)}
                            rows={10}
                            placeholder="Escribe el mensaje aquí..."
                            readOnly={isCompleted}
                        />
                    </div>
                </div>

                {/* Card 4: Comprobantes de Pago e Historial */}
                <div className="tk-table-card">
                    {cardHeader('Comprobantes de Pago e Historial')}
                    <div style={{ padding: '1rem' }} className="flex flex-column gap-3">

                        {/* Adjuntar nuevos */}
                        {!isCompleted && (
                            <div className="flex align-items-center gap-2 flex-wrap">
                                <button
                                    type="button"
                                    className="fk-btn fk-btn--secondary fk-btn--sm"
                                    onClick={() => fileInputRef.current?.click()}
                                    style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}
                                >
                                    <i className="pi pi-paperclip" style={{ fontSize: '0.72rem' }} />
                                    Adjuntar documentos
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    accept="application/pdf,image/*"
                                    style={{ display: 'none' }}
                                    onChange={handleFileSelect}
                                />
                                {uploadedFiles.length === 0 && (
                                    <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>Sin archivos adjuntos</span>
                                )}
                                {uploadedFiles.map((file, i) => (
                                    <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '99px', padding: '0.2rem 0.4rem 0.2rem 0.65rem', fontSize: '0.73rem', color: '#334155', maxWidth: '220px' }}>
                                        <i className="pi pi-file" style={{ fontSize: '0.68rem', flexShrink: 0 }} />
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                                        <span style={{ color: '#94a3b8', flexShrink: 0 }}>({(file.size / 1024).toFixed(0)} KB)</span>
                                        <button type="button" onClick={() => removeFile(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '0 0.1rem', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                            <i className="pi pi-times" style={{ fontSize: '0.62rem' }} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* Divisor entre carga e historial de archivos */}
                        {!isCompleted && (clientFiles.length > 0 || adminFiles.length > 0) && (
                            <div style={{ height: '1px', background: '#e2e8f0' }} />
                        )}

                        {/* Historial de Archivos: Cliente */}
                        {clientFiles.length > 0 && (
                            <div>
                                <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.72rem', fontWeight: 700, color: '#475569', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.25rem' }}>
                                    Comprobantes del Cliente
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                    {clientFiles.map((file, index) => (
                                        <div key={index} className="flex align-items-center gap-2 p-2 border-1 border-blue-200 border-round bg-blue-50 shadow-1" style={{ minWidth: '200px' }}>
                                            <i className={`pi ${file.file_name?.endsWith('.pdf') ? 'pi-file-pdf text-red-500' : 'pi-image text-blue-500'} text-xl`}></i>
                                            <div className="flex flex-column" style={{ maxWidth: '160px' }}>
                                                <span className="text-sm font-medium text-gray-800 white-space-nowrap overflow-hidden text-overflow-ellipsis" title={file.file_name}>{file.file_name}</span>
                                                <span className="text-xs text-gray-500">{new Date(file.created_at).toLocaleDateString()}</span>
                                            </div>
                                            {file.s3_url && (
                                                <a href={file.s3_url} target="_blank" rel="noopener noreferrer" className="ml-auto flex align-items-center p-1 hover:bg-gray-100 border-round">
                                                    <i className="pi pi-eye text-blue-600 cursor-pointer"></i>
                                                </a>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Historial de Archivos: Administrador */}
                        {adminFiles.length > 0 && (
                            <div>
                                <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.72rem', fontWeight: 700, color: '#475569', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.25rem' }}>
                                    Archivos del Administrador
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                    {adminFiles.map((file, index) => (
                                        <div key={index} className="flex align-items-center gap-2 p-2 border-1 border-gray-300 border-round bg-white shadow-1" style={{ minWidth: '200px' }}>
                                            <i className={`pi ${file.file_name?.endsWith('.pdf') ? 'pi-file-pdf text-red-500' : 'pi-image text-blue-500'} text-xl`}></i>
                                            <div className="flex flex-column" style={{ maxWidth: '160px' }}>
                                                <span className="text-sm font-medium text-gray-800 white-space-nowrap overflow-hidden text-overflow-ellipsis" title={file.file_name}>{file.file_name}</span>
                                                <span className="text-xs text-gray-500">{new Date(file.created_at).toLocaleDateString()}</span>
                                            </div>
                                            {file.s3_url && (
                                                <a href={file.s3_url} target="_blank" rel="noopener noreferrer" className="ml-auto flex align-items-center p-1 hover:bg-gray-100 border-round">
                                                    <i className="pi pi-eye text-blue-600 cursor-pointer"></i>
                                                </a>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Historial de Pagos */}
                        {hasPaymentsHistory && (
                            <>
                                <div style={{ height: '1px', background: '#e2e8f0' }} />
                                <div>
                                    <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.72rem', fontWeight: 700, color: '#475569', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.25rem' }}>
                                        Historial de Pagos
                                    </h4>
                                    <div className="flex flex-column gap-2">
                                        {stepData.payments.map((payment) => (
                                            <div key={payment.id} className="flex justify-content-between align-items-center p-2 border-round"
                                                style={{ backgroundColor: payment.status === 'complete' ? '#D1F1E6' : payment.status === 'rejected' ? '#F8D7DA' : '#F8F9FA' }}>
                                                <div className="flex flex-column">
                                                    <span className="font-medium text-gray-800">
                                                        {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(payment.amount)}
                                                    </span>
                                                    <span className="text-sm text-gray-500">
                                                        {new Date(payment.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                    </span>
                                                </div>
                                                <div>
                                                    {payment.status === 'pending' && <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 border-round font-bold uppercase">Evaluando</span>}
                                                    {payment.status === 'complete' && <span className="bg-green-100 text-green-800 text-xs px-2 py-1 border-round font-bold uppercase">Verificado</span>}
                                                    {payment.status === 'incomplete' && <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 border-round font-bold uppercase">Incompleto</span>}
                                                    {payment.status === 'rejected' && <span className="bg-red-100 text-red-800 text-xs px-2 py-1 border-round font-bold uppercase">Rechazado</span>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Resumen de Totales */}
                                <div style={{ height: '1px', background: '#e2e8f0' }} />
                                <div className="flex flex-column gap-2">
                                    <div className="flex justify-content-between align-items-center">
                                        <span className="font-bold text-gray-700">Monto Total:</span>
                                        <span className="font-bold text-blue-600 text-lg">
                                            {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Math.round((persona?.infoBudget?.financial_details?.groupConceptsEnganche.totalWitIva || 0) * 100) / 100)}
                                        </span>
                                    </div>
                                    <div className="flex justify-content-between align-items-center">
                                        <span className="font-bold text-gray-700">Total Restante:</span>
                                        <span className="font-bold text-blue-600 text-lg">
                                            {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(totalRemaining())}
                                        </span>
                                    </div>
                                    <div className="flex justify-content-between align-items-center">
                                        <span className="font-bold text-gray-700">Total Pagado:</span>
                                        <span className="font-bold text-blue-600 text-lg">
                                            {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(totalPaid())}
                                        </span>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Botones de acción */}
                <div className="flex justify-content-end gap-2 mt-2 pt-3 border-top-1 border-gray-200">
                    <Button
                        label="Cancelar"
                        icon="pi pi-times"
                        className="p-button-outlined p-button-secondary"
                        onClick={onHide}
                        disabled={sending}
                    />
                    {!isCompleted && (
                        <>
                            {subject.includes('rechazado') ? (
                                <Button
                                    label={sending ? "Rechazando..." : "Confirmar Rechazo"}
                                    icon={sending ? "pi pi-spin pi-spinner" : "pi pi-times"}
                                    className="p-button-danger"
                                    onClick={handleRejectPayment}
                                    disabled={sending}
                                />
                            ) : (
                                <Button
                                    label={sending ? "Enviando..." : (stepData?.payments?.some(p => p.status === 'pending') && uploadedFiles.length === 0 ? "Confirmar y Enviar" : "Subir y Enviar")}
                                    icon={sending ? "pi pi-spin pi-spinner" : "pi pi-send"}
                                    className="Button Green"
                                    onClick={handleSendEmail}
                                    disabled={sending || !verifiedAmount || parseFloat(verifiedAmount) <= 0 || !email || (uploadedFiles.length === 0 && !stepData?.payments?.some(p => p.status === 'pending'))}
                                    loading={sending}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </Dialog >
    );
};
